import { useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSubmitApplication } from "@/api/hooks/mutations";
import { ApiError } from "@/api/client";
import { Button, buttonClass } from "@/components/ui/Button";
import { ChipInput } from "@/components/ui/ChipInput";
import { Field, fieldInputClass } from "@/components/ui/Field";
import { FileUploader } from "@/components/ui/FileUploader";

const openApplicationSchema = z.object({
  full_name: z.string().min(2, "Tell us your full name"),
  email: z.string().email("Enter a valid email"),
  phone: z.string().min(4, "Phone is required"),
  location: z.string().optional().or(z.literal("")),
  linkedin_url: z.string().optional().or(z.literal("")),
  telegram_url: z.string().optional().or(z.literal("")),
  github_url: z.string().optional().or(z.literal("")),
  cover_letter: z.string().min(10, "A short intro helps"),
  years_experience: z.string().min(1, "Pick one"),
  current_role: z.string().optional().or(z.literal("")),
  expected_rate: z.string().min(1, "Required"),
  engagement_pref: z.string().optional().or(z.literal("")),
  earliest_start: z.string().optional().or(z.literal("")),
  notice_period: z.string().optional().or(z.literal("")),
  additional_info: z.string().optional().or(z.literal("")),
  consent: z.literal(true, { message: "Consent is required" }),
  website: z.string().max(0).optional().or(z.literal("")),
});

type OpenApplicationInput = z.infer<typeof openApplicationSchema>;

export function OpenApplicationPage() {
  const submit = useSubmitApplication();
  const [skills, setSkills] = useState<string[]>([]);
  const [cv, setCv] = useState<File | null>(null);
  const [cvInvalid, setCvInvalid] = useState(false);
  const [skillsInvalid, setSkillsInvalid] = useState(false);
  const [topError, setTopError] = useState("");
  const [done, setDone] = useState(false);

  const form = useForm<OpenApplicationInput>({
    resolver: zodResolver(openApplicationSchema),
    defaultValues: { years_experience: "6-9 years" },
  });

  async function onSubmit(values: OpenApplicationInput) {
    setTopError("");
    if (values.website) {
      setDone(true);
      return;
    }
    if (!skills.length) {
      setSkillsInvalid(true);
      setTopError("Please list at least one skill.");
      return;
    }
    if (!cv) {
      setCvInvalid(true);
      setTopError("Please attach your CV.");
      return;
    }
    setSkillsInvalid(false);
    setCvInvalid(false);

    const fd = new FormData();
    fd.set("full_name", values.full_name);
    fd.set("email", values.email);
    fd.set("phone", values.phone);
    fd.set("cover_letter", values.cover_letter);
    fd.set("years_experience", values.years_experience);
    fd.set("expected_rate", values.expected_rate);
    fd.set("currency", "USD");
    fd.set("consent", values.consent ? "true" : "false");
    if (values.location) fd.set("location", values.location);
    if (values.linkedin_url) fd.set("linkedin_url", values.linkedin_url);
    if (values.telegram_url) fd.set("telegram_url", values.telegram_url);
    if (values.github_url) fd.set("github_url", values.github_url);
    if (values.current_role) fd.set("current_role", values.current_role);
    if (values.engagement_pref) fd.set("engagement_pref", values.engagement_pref);
    if (values.earliest_start) fd.set("earliest_start", values.earliest_start);
    if (values.notice_period) fd.set("notice_period", values.notice_period);
    if (values.additional_info) fd.set("additional_info", values.additional_info);
    fd.set("skills", skills.join(", "));
    fd.set("website", "");
    fd.set("cv", cv);

    try {
      await submit.mutateAsync(fd);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 415)
          setTopError("That file type isn't supported. PDF, DOC or DOCX please.");
        else if (err.status === 413)
          setTopError("File is too large — keep your CV under 10 MB.");
        else if (err.status === 429)
          setTopError("Too many submissions — please try again in a minute.");
        else setTopError(err.message || "Could not submit your application.");
      } else {
        setTopError("Network error. Please try again.");
      }
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-[1100px] px-4 py-12 sm:px-6 sm:py-16">
        <div className="rounded-2xl border border-border bg-surface p-6 text-center sm:p-10">
          <h1 className="text-2xl font-bold">Application submitted</h1>
          <p className="mt-2 text-text-muted">
            Thanks — we will review your profile and contact you when there is a relevant match.
          </p>
          <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row">
            <Link to="/jobs" className={`${buttonClass()} w-full sm:w-auto`}>
              Browse open roles
            </Link>
            <Link to="/" className={`${buttonClass({ variant: "secondary" })} w-full sm:w-auto`}>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const consentClass = form.formState.errors.consent
    ? "mt-4 flex items-start gap-2.5 rounded-md bg-danger-soft p-3.5 outline outline-1 outline-danger"
    : "mt-4 flex items-start gap-2.5 rounded-md bg-surface-alt p-3.5";

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-10 sm:px-6 sm:py-12">
      <div className="mb-3 flex flex-wrap gap-x-1 text-sm text-text-muted">
        <Link to="/" className="hover:text-text">
          Home
        </Link>{" "}
        ›{" "}
        <Link to="/jobs" className="hover:text-text">
          Find a job
        </Link>{" "}
        › Open application
      </div>

      <div className="mb-6 rounded-2xl border border-border bg-surface p-6">
        <div className="max-w-[720px]">
          <span className="mb-3 inline-flex rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary-hover">
            Talent network
          </span>
          <h1 className="text-2xl font-bold tracking-tight">Open application</h1>
          <p className="mt-2 text-sm leading-relaxed text-text-muted">
            Send your profile even if there is no matching role listed today. We keep strong
            specialists in a private talent network and contact them when a relevant outstaffing
            or recruiting opportunity appears.
          </p>
        </div>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="grid items-start gap-6 lg:grid-cols-[1fr_320px]">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...form.register("website")}
          className="absolute -left-[9999px]"
          aria-hidden
        />

        <div className="flex flex-col gap-5">
          <Card title="About you" hint="We use this to contact you about relevant matches.">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Full name" required error={form.formState.errors.full_name?.message}>
                <input
                  type="text"
                  placeholder="e.g. Jordan Smith"
                  {...form.register("full_name")}
                  className={fieldInputClass(!!form.formState.errors.full_name)}
                />
              </Field>
              <Field label="Email" required error={form.formState.errors.email?.message}>
                <input
                  type="email"
                  placeholder="jordan@example.com"
                  {...form.register("email")}
                  className={fieldInputClass(!!form.formState.errors.email)}
                />
              </Field>
              <Field label="Phone" required error={form.formState.errors.phone?.message}>
                <input
                  type="tel"
                  placeholder="+1 555 0100"
                  {...form.register("phone")}
                  className={fieldInputClass(!!form.formState.errors.phone)}
                />
              </Field>
              <Field label="Location">
                <input
                  type="text"
                  placeholder="e.g. Yerevan, Armenia (UTC+4)"
                  {...form.register("location")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="LinkedIn">
                <input
                  type="url"
                  placeholder="linkedin.com/in/..."
                  {...form.register("linkedin_url")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Telegram">
                <input
                  type="text"
                  placeholder="@username or t.me/username"
                  {...form.register("telegram_url")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="GitHub">
                <input
                  type="url"
                  placeholder="github.com/..."
                  {...form.register("github_url")}
                  className={fieldInputClass()}
                />
              </Field>
            </div>
          </Card>

          <Card title="CV / Resume *" hint="PDF, DOC or DOCX up to 10 MB.">
            <FileUploader
              value={cv}
              onChange={(f) => {
                setCv(f);
                if (f) setCvInvalid(false);
              }}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              invalid={cvInvalid}
            />
          </Card>

          <Card title="Experience and skills" hint="Help us understand where you fit best.">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                label="Short intro"
                required
                error={form.formState.errors.cover_letter?.message}
                className="sm:col-span-2"
              >
                <textarea
                  placeholder="What roles are you strongest for? What kind of projects do you want next?"
                  {...form.register("cover_letter")}
                  className={fieldInputClass(!!form.formState.errors.cover_letter) + " min-h-[150px] resize-y"}
                />
              </Field>
              <Field label="Years of experience" required>
                <select {...form.register("years_experience")} className={fieldInputClass()}>
                  <option value="0-2 years">0-2 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="6-9 years">6-9 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </Field>
              <Field label="Current role">
                <input
                  type="text"
                  placeholder="e.g. Senior Backend Engineer"
                  {...form.register("current_role")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Top skills" required className="sm:col-span-2">
                <ChipInput
                  ariaLabel="Top skills"
                  value={skills}
                  onChange={(v) => {
                    setSkills(v);
                    if (v.length) setSkillsInvalid(false);
                  }}
                  placeholder="Add and press Enter..."
                  invalid={skillsInvalid}
                />
              </Field>
            </div>
          </Card>

          <Card title="Rate and availability">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                label="Expected rate"
                required
                error={form.formState.errors.expected_rate?.message}
                hint="Hourly or monthly is fine."
              >
                <input
                  type="text"
                  placeholder="e.g. $40/hour or $6,500/month"
                  {...form.register("expected_rate")}
                  className={fieldInputClass(!!form.formState.errors.expected_rate)}
                />
              </Field>
              <Field label="Preferred engagement">
                <select {...form.register("engagement_pref")} className={fieldInputClass()}>
                  <option value="">—</option>
                  <option value="outstaffing">Outstaffing</option>
                  <option value="recruiting">Recruiting / direct hire</option>
                  <option value="both">Both</option>
                  <option value="project_based">Project-based</option>
                </select>
              </Field>
              <Field label="Earliest start date">
                <input
                  type="text"
                  placeholder="e.g. Available immediately"
                  {...form.register("earliest_start")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Notice period">
                <select {...form.register("notice_period")} className={fieldInputClass()}>
                  <option value="">—</option>
                  <option value="immediate">Available immediately</option>
                  <option value="2 weeks">2 weeks</option>
                  <option value="1 month">1 month</option>
                  <option value="2+ months">2+ months</option>
                </select>
              </Field>
              <Field label="Anything else?" className="sm:col-span-2">
                <textarea
                  placeholder="English level, timezone, preferred industries, availability, or sharing rules."
                  {...form.register("additional_info")}
                  className={fieldInputClass() + " min-h-[100px] resize-y"}
                />
              </Field>
            </div>

            <label className={consentClass}>
              <input
                type="checkbox"
                {...form.register("consent")}
                className="mt-0.5 size-4 accent-primary"
              />
              <span className="text-[13px] leading-relaxed text-text-muted">
                I agree that NgonTech may store and process my application data to match me with
                relevant opportunities. My CV/profile will not be shared with clients without a
                relevant match and appropriate follow-up.
              </span>
            </label>

            {topError && (
              <div className="mt-4 rounded-md bg-danger-soft px-3 py-2 text-sm text-[#b91c1c]">
                {topError}
              </div>
            )}

            <div className="mt-5 flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-end">
              <Link to="/jobs" className={`${buttonClass({ variant: "secondary" })} w-full sm:w-auto`}>
                Cancel
              </Link>
              <Button type="submit" loading={form.formState.isSubmitting} className="w-full sm:w-auto">
                Submit open application
              </Button>
            </div>
          </Card>
        </div>

        <aside className="flex flex-col gap-4 lg:sticky lg:top-[88px]">
          <Card title="What happens next">
            <ol className="flex flex-col gap-3 text-[13px]">
              <Step n={1}>We review your profile and CV.</Step>
              <Step n={2}>If there is a fit, we contact you before sharing details externally.</Step>
              <Step n={3}>When a matching client role appears, we align on rate and availability.</Step>
            </ol>
          </Card>
          <Card title="Private talent network">
            <p className="text-[13px] leading-relaxed text-text-muted">
              This is not a public profile. It helps NgonTech privately match strong specialists
              with outstaffing and recruiting opportunities.
            </p>
          </Card>
        </aside>
      </form>
    </div>
  );
}

function Card({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-6">
      <h2 className="mb-1 text-base font-semibold">{title}</h2>
      {hint && <p className="mb-5 text-[13px] text-text-muted">{hint}</p>}
      {children}
    </section>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="grid size-[22px] shrink-0 place-items-center rounded-full bg-primary-soft text-[11px] font-bold text-primary-hover">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}
